// src/pages/personal/analytics/FeedbackSection/components/FeedbackMetrics.tsx
import React from 'react';
import { useTranslation } from 'react-i18next';
import { MetricCard } from '@/components/ui/Layout';
import { formatNumber, formatPercent } from '@/utils/Formatters';
import { NUMBER_FORMATS, LOCALES } from '@/utils/constants';

// Типы для метрик
type FeedbackMetricKey = 'product_quality' | 'service' | 'value' | 'recommend_percentage';

interface Metric {
  value: number;
  change: number;
  trend: 'up' | 'down' | 'neutral';
}

interface FeedbackMetricsProps {
  data?: Record<string, Metric>; // Добавляем для совместимости
  metrics?: Record<FeedbackMetricKey, Metric>;
  onSelectMetric?: (metric: FeedbackMetricKey) => void;
  selectedMetric?: FeedbackMetricKey;
  isLoading?: boolean;
}

const FeedbackMetrics: React.FC<FeedbackMetricsProps> = ({ 
  data,
  metrics, 
  onSelectMetric, 
  selectedMetric = 'product_quality', 
  isLoading = false 
}) => {
  const { t, i18n } = useTranslation('analytics');
  const currentLocale = i18n.language || LOCALES.DEFAULT;
  
  // Используем либо metrics, либо data для обратной совместимости
  // Указываем явно тип как Record<string, Metric> с пустым объектом по умолчанию
  const metricsData: Record<string, Metric> = metrics || data || {};
  
  // Добавляем список ключей для перебора при отсутствии metrics
  const metricKeys = ['product_quality', 'service', 'value', 'recommend_percentage'];
  
  // Функция для форматирования значения в зависимости от типа метрики
  const formatMetricValue = (key: FeedbackMetricKey, value: number): string => {
    if (isLoading || value === undefined || value === null || isNaN(value)) return '-';
    
    switch (key) {
      case 'product_quality':
      case 'service':
      case 'value':
        // Убедимся, что значение не превышает 3 (для 3-балльной шкалы)
        const normalizedValue = Math.min(value, 3);
        return formatNumber(normalizedValue, NUMBER_FORMATS.DEFAULT_PRECISION, true, currentLocale);
      case 'recommend_percentage':
        return formatPercent(value, 1, true, currentLocale); // Убираем деление на 100
      default:
        return formatNumber(value, NUMBER_FORMATS.DEFAULT_PRECISION, true, currentLocale);
    }
  };
  
  // Проверяем, влияет ли метрика на график
  const isGraphMetric = (key: string): boolean => {
    return ['product_quality', 'service', 'value'].includes(key);
  };
  
  // Обработчик клика по метрике
  const handleMetricClick = (metric: FeedbackMetricKey) => {
    if (onSelectMetric) {
      onSelectMetric(metric);
    }
  };
  
  return (
    <div className="w-full">
      <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-4 w-full">
        {metrics 
          ? Object.entries(metricsData).map(([key, metric]) => {
              const metricKey = key as FeedbackMetricKey;
              // Теперь TypeScript знает, что metric имеет тип Metric
              const value = metric.value ?? 0;
              const change = metric.change ?? 0;
              const canSelectMetric = isGraphMetric(key);
              
              return (
                <MetricCard
                  key={key}
                  title={t(`feedback.metrics.${key}`)}
                  value={formatMetricValue(metricKey, value)}
                  trend={metric.trend ?? 'neutral'}
                  trendValue={change}
                  isSelected={selectedMetric === metricKey}
                  onClick={canSelectMetric && handleMetricClick ? () => handleMetricClick(metricKey) : undefined}
                  className={canSelectMetric ? "cursor-pointer" : ""}
                />
              );
            })
          : metricKeys.map(key => {
              // Используем явную проверку и получаем значение метрики безопасно
              const metric = (key in metricsData) ? metricsData[key] as Metric : undefined;
              const value = metric?.value ?? 0;
              const change = metric?.change ?? 0;
              const trend = metric?.trend ?? 'neutral';
              const canSelectMetric = isGraphMetric(key);
              
              return (
                <MetricCard
                  key={key}
                  title={t(`feedback.metrics.${key}`)}
                  value={formatMetricValue(key as FeedbackMetricKey, value)}
                  trend={trend}
                  trendValue={change}
                  isSelected={selectedMetric === key as FeedbackMetricKey}
                  onClick={canSelectMetric && handleMetricClick ? () => handleMetricClick(key as FeedbackMetricKey) : undefined}
                  className={canSelectMetric ? "cursor-pointer" : ""}
                />
              );
            })
        }
      </div>
    </div>
  );
};

export default FeedbackMetrics;